import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { getTheme, getDensity } from './theme';
import { formatDate, fmtINR, fmtNum } from './calculations';
import { generateDemoData } from './demoData';
import { useFrozenSort } from './useFrozenSort';
import { downloadRowsAsCSV } from './csvExport';
import { ConnectionBanner, TopBar } from './StatusBar';
import FilterBar from './FilterBar';
import MarketWatchLoading from './MarketWatchLoading';
import OrderPopup from './OrderPopup';
import DataTable, { COLUMNS } from './DataTable';
import useColumnLayout from './useColumnLayout';
import { ColumnsButton } from './ColumnsPanel';

const API_BASE = '/api';

// ═══════════════════════════════════════════════════════════════
// MARKET WATCH — Main Component
// ═══════════════════════════════════════════════════════════════

export default function MarketWatch({ demoMode: appDemoMode, sessionId, user }) {
  // ─── Theme & Display Settings ──────────────────────────────
  const [theme, setTheme] = useState(() => localStorage.getItem('mw_theme') || 'dark');
  const [density, setDensity] = useState(() => localStorage.getItem('mw_density') || 'comfortable');

  // ─── Data ──────────────────────────────────────────────────
  const [initialized, setInitialized] = useState(false);
  const [brokerMessage, setBrokerMessage] = useState(''); // Message from backend when not initialized
  const [options, setOptions] = useState([]);           // Static option metadata from /market-watch/data
  const [computedRows, setComputedRows] = useState([]); // Pre-computed rows from backend (Problem #40)
  const [margins, setMargins] = useState({});            // Still needed for OrderPopup margin display
  const [marginFetchMode, setMarginFetchMode] = useState('batch');
  const [results, setResults] = useState({});
  const [accountMargins, setAccountMargins] = useState({});
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [connStatus, setConnStatus] = useState(null);
  // Session 46 — WS indicator: token IDs currently subscribed to Kite WebSocket
  // (real-time). Updated via 'wsTokens' SSE event. Tokens NOT in this Set are on
  // 30-second REST polling. DataTable uses this to render a small green dot (live)
  // or hollow grey circle (polled) next to the LTP value.
  const [liveTokens, setLiveTokens] = useState(() => new Set());
  // Session 46 — Positions + Orders polling: aggregated across all user-accessible
  // accounts (Kite + Kotak). Polled every 30 seconds. Used to render badges next
  // to the Script column showing user's existing positions/orders for that symbol.
  const [myPositions, setMyPositions] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  // Session 46 — 3-state position filter switch (cycles: 'all' → 'with' → 'without' → 'all').
  const [positionFilter, setPositionFilter] = useState(() => localStorage.getItem('mw_positionFilter') || 'all');
  // Session 46 — Best-per-underlying-per-type toggle. When ON, after all other filters,
  // keeps only the row with the highest annualized return per (symbol, CE/PE) combo.
  const [bestPerUnderlying, setBestPerUnderlying] = useState(() => localStorage.getItem('mw_bestPerUnderlying') === '1');

  // ─── Filters ───────────────────────────────────────────────
  const [search, setSearch] = useState(() => localStorage.getItem('mw_search') || '');
  const [expiryFilter, setExpiryFilter] = useState(() => { try { return JSON.parse(localStorage.getItem('mw_expiry')) || []; } catch { return []; } });
  const [typeFilter, setTypeFilter] = useState(() => { try { return JSON.parse(localStorage.getItem('mw_type')) || []; } catch { return []; } });
  const [moneyFilter, setMoneyFilter] = useState(() => { try { return JSON.parse(localStorage.getItem('mw_money')) || []; } catch { return []; } });
  const [sectorFilter, setSectorFilter] = useState(() => { try { return JSON.parse(localStorage.getItem('mw_sector')) || []; } catch { return []; } });
  const [categoryFilter, setCategoryFilter] = useState(() => { try { return JSON.parse(localStorage.getItem('mw_category')) || []; } catch { return []; } });
  const [publicPrivateFilter, setPublicPrivateFilter] = useState(() => { try { return JSON.parse(localStorage.getItem('mw_pubpvt')) || []; } catch { return []; } });
  const [ssMin, setSSMin] = useState(() => localStorage.getItem('mw_ssMin') || '');
  const [ssMax, setSSMax] = useState(() => localStorage.getItem('mw_ssMax') || '');
  const [oiMin, setOiMin] = useState(() => localStorage.getItem('mw_oiMin') || '');
  const [volMin, setVolMin] = useState(() => localStorage.getItem('mw_volMin') || '');
  const [resultDateFrom, setResultDateFrom] = useState(() => localStorage.getItem('mw_resultFrom') || '');
  const [resultDateTo, setResultDateTo] = useState(() => localStorage.getItem('mw_resultTo') || '');

  // ─── Sort ──────────────────────────────────────────────────
  const [sortCol, setSortCol] = useState(() => localStorage.getItem('mw_sortCol') || 'symbol');
  const [sortDir, setSortDir] = useState(() => localStorage.getItem('mw_sortDir') || 'asc');
  // Sort-on-demand: row values update every second but row ORDER stays frozen.
  // Sorting only happens when: user clicks Re-sort, changes sort column/direction, or every 60s auto.
  const [sortVersion, setSortVersion] = useState(0);  // Bump to trigger a re-sort
  const autoSortTimerRef = useRef(null);

  // ─── Order Popup ───────────────────────────────────────────
  const [orderPopup, setOrderPopup] = useState(null);
  const [orderAccount, setOrderAccount] = useState('');
  const [orderMarginData, setOrderMarginData] = useState(null);
  const [orderLots, setOrderLots] = useState(1);
  const [orderPrice, setOrderPrice] = useState('');

  // ─── Refs ──────────────────────────────────────────────────
  const eventSourceRef = useRef(null);     // SSE connection (Problem #40 Phase 2)
  const tokenIndexRef = useRef(new Map()); // token -> array index for fast delta merges
  const fullDataInterval = useRef(null);
  const accountInterval = useRef(null);
  const connStatusInterval = useRef(null);
  const optionsMapRef = useRef(new Map());
  const loadingRef = useRef(false);        // Lock to prevent concurrent loadFullData() calls
  const sseReconnectTimer = useRef(null);  // Controlled SSE reconnect (no browser auto-reconnect)
  const sseReconnectCount = useRef(0);     // Track reconnect attempts

  // ─── SSE throttle buffer ───────────────────────────────────
  // Instead of calling setComputedRows 2-3 times per second (delta + spots),
  // we buffer all changes in a mutable ref and flush ONCE per second.
  // This avoids creating multiple 39,000-row array copies per second (saves ~2-3GB RAM).
  const rowsBufferRef = useRef([]);        // The working copy of computedRows (mutated in-place)
  const bufferDirtyRef = useRef(false);    // Whether buffer has changes that need flushing
  const flushTimerRef = useRef(null);      // 1-second flush interval

  // ─── Persist settings ──────────────────────────────────────
  useEffect(() => { localStorage.setItem('mw_theme', theme); }, [theme]);
  useEffect(() => { localStorage.setItem('mw_density', density); }, [density]);
  useEffect(() => { localStorage.setItem('mw_search', search); }, [search]);
  useEffect(() => { localStorage.setItem('mw_expiry', JSON.stringify(expiryFilter)); }, [expiryFilter]);
  useEffect(() => { localStorage.setItem('mw_type', JSON.stringify(typeFilter)); }, [typeFilter]);
  useEffect(() => { localStorage.setItem('mw_money', JSON.stringify(moneyFilter)); }, [moneyFilter]);
  useEffect(() => { localStorage.setItem('mw_sector', JSON.stringify(sectorFilter)); }, [sectorFilter]);
  useEffect(() => { localStorage.setItem('mw_category', JSON.stringify(categoryFilter)); }, [categoryFilter]);
  useEffect(() => { localStorage.setItem('mw_pubpvt', JSON.stringify(publicPrivateFilter)); }, [publicPrivateFilter]);
  useEffect(() => { localStorage.setItem('mw_ssMin', ssMin); }, [ssMin]);
  useEffect(() => { localStorage.setItem('mw_ssMax', ssMax); }, [ssMax]);
  useEffect(() => { localStorage.setItem('mw_sortCol', sortCol); }, [sortCol]);
  useEffect(() => { localStorage.setItem('mw_sortDir', sortDir); }, [sortDir]);
  useEffect(() => { localStorage.setItem('mw_oiMin', oiMin); }, [oiMin]);
  useEffect(() => { localStorage.setItem('mw_volMin', volMin); }, [volMin]);
  useEffect(() => { localStorage.setItem('mw_resultFrom', resultDateFrom); }, [resultDateFrom]);
  useEffect(() => { localStorage.setItem('mw_resultTo', resultDateTo); }, [resultDateTo]);
  useEffect(() => { localStorage.setItem('mw_positionFilter', positionFilter); }, [positionFilter]);
  useEffect(() => { localStorage.setItem('mw_bestPerUnderlying', bestPerUnderlying ? '1' : '0'); }, [bestPerUnderlying]);

  // ─── Demo Mode — controlled by App.js via appDemoMode prop ───

  // ═══════════════════════════════════════════════════════════
  // DATA LOADING
  // ═══════════════════════════════════════════════════════════

  useEffect(() => {
    loadFullData();
    // Auto re-sort every 60 seconds so rows don't stay frozen forever
    autoSortTimerRef.current = setInterval(() => setSortVersion(v => v + 1), 60000);
    return () => {
      if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null; }
      if (flushTimerRef.current) { clearInterval(flushTimerRef.current); flushTimerRef.current = null; }
      if (fullDataInterval.current) clearInterval(fullDataInterval.current);
      if (accountInterval.current) clearInterval(accountInterval.current);
      if (connStatusInterval.current) clearInterval(connStatusInterval.current);
      if (autoSortTimerRef.current) clearInterval(autoSortTimerRef.current);
      if (sseReconnectTimer.current) { clearTimeout(sseReconnectTimer.current); sseReconnectTimer.current = null; }
    };
  }, []);

  async function loadFullData() {
    // Prevent concurrent calls — if already loading, skip
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      const res = await axios.get(`${API_BASE}/market-watch/data`);
      if (!res.data.initialized) {
        setBrokerMessage(res.data.message || 'Waiting for broker login...');
        return;
      }
      setBrokerMessage('');
      const loadedOptions = res.data.options || [];
      setOptions(loadedOptions);

      // Build token -> option lookup for merging with slim computed rows (Problem #40)
      const map = new Map();
      loadedOptions.forEach(o => map.set(o.token, o));
      optionsMapRef.current = map;

      setMargins(res.data.margins || {});
      setResults(res.data.results || {});
      setInitialized(true);

      if (loadedOptions.length > 0) {
        const validExpiries = new Set(loadedOptions.map(o => o.expiryStr || o.expiry));
        const validSectors = new Set(loadedOptions.map(o => o.sector).filter(Boolean));
        const validCategories = new Set(loadedOptions.map(o => o.category).filter(Boolean));
        if (expiryFilter.length > 0) { const valid = expiryFilter.filter(v => validExpiries.has(v)); if (valid.length !== expiryFilter.length) setExpiryFilter(valid); }
        if (sectorFilter.length > 0) { const valid = sectorFilter.filter(v => validSectors.has(v)); if (valid.length !== sectorFilter.length) setSectorFilter(valid); }
        if (categoryFilter.length > 0) { const valid = categoryFilter.filter(v => validCategories.has(v)); if (valid.length !== categoryFilter.length) setCategoryFilter(valid); }
      }

      // Connect SSE for real-time delta pushes (Problem #40 Phase 2)
      // Replaces polling /computed-rows every 1 second (13MB per poll).
      // Server sends a full snapshot on connect, then pushes only changed rows (~100-200KB).
      connectSSE();

      // Margin trigger: wait for backend to have spot prices before refreshing margin data
      let marginTriggerAttempts = 0;
      const marginTriggerInterval = setInterval(async () => {
        marginTriggerAttempts++;
        try {
          // Check if computed rows have spot prices (means WebSocket is connected)
          if (computedRows.length > 0 && computedRows.some(r => r.spot > 0)) {
            clearInterval(marginTriggerInterval);
            const dataRes = await axios.get(`${API_BASE}/market-watch/data`);
            if (dataRes.data.initialized) setMargins(dataRes.data.margins || {});
          } else if (marginTriggerAttempts >= 12) {
            clearInterval(marginTriggerInterval);
          }
        } catch (e) { clearInterval(marginTriggerInterval); }
      }, 5000);

      if (fullDataInterval.current) clearInterval(fullDataInterval.current);
      fullDataInterval.current = setInterval(pollMargins, 60000);

      pollAccountMargins();
      if (accountInterval.current) clearInterval(accountInterval.current);
      accountInterval.current = setInterval(pollAccountMargins, 30000);

      pollConnStatus();
      if (connStatusInterval.current) clearInterval(connStatusInterval.current);
      connStatusInterval.current = setInterval(pollConnStatus, 5000);

      pollStatus();
    } catch (err) { console.error('Backend not available:', err.message); }
    finally { loadingRef.current = false; }
  }

  function loadDemoData() {
    const demo = generateDemoData();
    setOptions(demo.options);
    setMargins(demo.margins);
    setResults(demo.results);
    setAccountMargins(demo.accountMargins);
    setConnectionStatus({ kiteWebSocket: { connected: false, wsTokenCount: 0 } });
    setInitialized(true);
    // In demo mode, compute rows locally since there's no backend
    // Import computeRow only for demo — normal mode uses backend-computed rows
    import('./calculations').then(calc => {
      const demoRows = demo.options.map(o => calc.computeRow(o, demo.ticks, demo.margins, demo.results));
      setComputedRows(demoRows);
    });
  }

  // Problem #40 Phase 2 + Problem #53 fix: SSE connection for real-time delta pushes
  // Uses manual reconnect with backoff instead of browser's EventSource auto-reconnect.
  // Browser auto-reconnect was causing reconnect storms on cloud (100% CPU).
  //
  // THROTTLING: SSE events (delta + spots) arrive 2-3 times per second.
  // Instead of calling setComputedRows each time (which copies 39,000 rows and triggers
  // React re-render), we mutate the buffer ref in-place and flush to React ONCE per second.
  // This reduces memory from ~3GB to ~500MB and eliminates the out-of-memory crash.
  function connectSSE() {
    // Close existing connection if any
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    // Clear any pending reconnect timer
    if (sseReconnectTimer.current) {
      clearTimeout(sseReconnectTimer.current);
      sseReconnectTimer.current = null;
    }
    window.__sseSnapshotReceived = false; // Reset — waiting for new snapshot
    if (flushTimerRef.current) {
      clearInterval(flushTimerRef.current);
      flushTimerRef.current = null;
    }

    const es = new EventSource(`${API_BASE}/market-watch/sse?sid=${sessionId || ''}`);
    eventSourceRef.current = es;

    // Full snapshot — sent in chunks of 5,000 rows to avoid choking the connection.
    let snapshotChunks = [];
    let snapshotExpectedChunks = 0;

    es.addEventListener('snapshot', (e) => {
      try {
        const data = JSON.parse(e.data);
        const chunkRows = data.rows || [];
        if (chunkRows.length === 0 && !data.totalChunks) return;

        snapshotExpectedChunks = data.totalChunks || 1;
        snapshotChunks.push(...chunkRows);

        // Wait until all chunks received
        if (snapshotChunks.length < data.count && (data.chunk + 1) < snapshotExpectedChunks) return;

        // All chunks received — merge with static option data
        const merged = snapshotChunks.map(slim => {
          const opt = optionsMapRef.current.get(slim.token);
          return opt ? { ...opt, ...slim, id: slim.token } : slim;
        });

        // Rebuild token → array index map for fast delta merges
        const indexMap = new Map();
        merged.forEach((row, i) => indexMap.set(row.token, i));
        tokenIndexRef.current = indexMap;

        // Initialize the mutable buffer with the snapshot data
        rowsBufferRef.current = merged;
        window.__sseSnapshotReceived = true; // Flag for DataTable to show "no filters" vs "loading"
        setComputedRows(merged); // Only direct setComputedRows call — one-time snapshot

        // SSE connected successfully — reset reconnect counter
        sseReconnectCount.current = 0;

        // Start the 1-second flush timer (buffers delta+spots, flushes to React once/sec)
        if (flushTimerRef.current) clearInterval(flushTimerRef.current);
        flushTimerRef.current = setInterval(() => {
          if (bufferDirtyRef.current) {
            bufferDirtyRef.current = false;
            setComputedRows([...rowsBufferRef.current]); // Shallow copy — triggers React re-render
          }
        }, 1000);

        // Reset for potential reconnect
        snapshotChunks = [];
        snapshotExpectedChunks = 0;
      } catch (err) { console.error('[SSE] Snapshot parse error:', err); }
    });

    // Delta — only changed rows. Mutates buffer in-place (no React re-render yet).
    es.addEventListener('delta', (e) => {
      try {
        const data = JSON.parse(e.data);
        const changedRows = data.rows || [];
        if (changedRows.length === 0) return;

        const buffer = rowsBufferRef.current;
        if (buffer.length === 0) return; // Snapshot not received yet
        const indexMap = tokenIndexRef.current;

        changedRows.forEach(slim => {
          const idx = indexMap.get(slim.token);
          if (idx !== undefined) {
            const opt = optionsMapRef.current.get(slim.token);
            buffer[idx] = opt ? { ...opt, ...slim, id: slim.token } : slim;
          }
        });

        bufferDirtyRef.current = true; // Mark for next flush
      } catch (err) { console.error('[SSE] Delta parse error:', err); }
    });

    // Spots — ~211 underlying prices. Mutates buffer in-place (no React re-render yet).
    es.addEventListener('spots', (e) => {
      try {
        const data = JSON.parse(e.data);
        const changedSpots = data.spots || [];
        if (changedSpots.length === 0) return;

        const buffer = rowsBufferRef.current;
        if (buffer.length === 0) return; // Snapshot not received yet

        const spotMap = new Map();
        changedSpots.forEach(s => spotMap.set(s.token, s.spot));

        for (let i = 0; i < buffer.length; i++) {
          const row = buffer[i];
          if (row.underlyingToken && spotMap.has(row.underlyingToken)) {
            const newSpot = spotMap.get(row.underlyingToken);
            if (row.spot !== newSpot) {
              buffer[i] = { ...row, spot: newSpot };
            }
          }
        }

        bufferDirtyRef.current = true; // Mark for next flush
      } catch (err) { console.error('[SSE] Spots parse error:', err); }
    });

    // Session 46 — WS indicator: backend sends the current ws-token set on connect
    // and whenever smart promotion adds/removes tokens. Frontend stores in a Set
    // for O(1) lookup in DataTable's renderCell to draw the live/polled dot.
    es.addEventListener('wsTokens', (e) => {
      try {
        const data = JSON.parse(e.data);
        const tokens = data.tokens || [];
        // Build a new Set — assignment triggers React re-render once per update.
        // Updates are rare (only on promote/demote), so this is cheap.
        setLiveTokens(new Set(tokens));
      } catch (err) { console.error('[SSE] wsTokens parse error:', err); }
    });

    // Margins — pushed when margin cache updates (batch complete or 30-min refresh).
    // Merges margin values into existing buffer rows by tradingsymbol match.
    es.addEventListener('margins', (e) => {
      try {
        const data = JSON.parse(e.data);
        const marginUpdates = data.margins || {}; // { tradingsymbol: { totalMargin, span, exposure } }
        const updateCount = Object.keys(marginUpdates).length;
        if (updateCount === 0) return;

        const buffer = rowsBufferRef.current;
        if (buffer.length === 0) return;

        // Also update the margins state for OrderPopup
        setMargins(prev => ({ ...prev, ...marginUpdates }));

        // Merge into buffer rows — match by tradingsymbol
        for (let i = 0; i < buffer.length; i++) {
          const row = buffer[i];
          const mData = marginUpdates[row.tradingsymbol];
          if (mData && mData.totalMargin !== undefined) {
            const newMargin = mData.totalMargin || 0;
            if (row.margin !== newMargin) {
              // Recalculate margin-dependent fields
              const premiumIncome = row.premiumIncome || 0;
              const returnPct = newMargin > 0 ? +((premiumIncome / newMargin) * 100).toFixed(2) : 0;
              let annualized = 0;
              if (row.daysToExpiry > 0 && returnPct > 0) {
                annualized = +((Math.pow(1 + Math.abs(returnPct / 100), 365 / row.daysToExpiry) - 1) * 100).toFixed(1);
              }
              const equalWeight = +(Math.abs(annualized * (row.strikeStockRatio || 0))).toFixed(1);
              buffer[i] = { ...row, margin: newMargin, returnPct, annualized, equalWeight };
            }
          }
        }

        bufferDirtyRef.current = true;
        console.log(`[SSE] Margins updated: ${updateCount} options`);
      } catch (err) { console.error('[SSE] Margins parse error:', err); }
    });

    // Error handler — NO browser auto-reconnect.
    // We close the EventSource and reconnect manually with backoff.
    es.onerror = () => {
      console.warn('[SSE] Connection error');
      // Close the broken connection so browser doesn't auto-reconnect
      es.close();
      eventSourceRef.current = null;

      // Stop the flush timer — no data coming
      if (flushTimerRef.current) {
        clearInterval(flushTimerRef.current);
        flushTimerRef.current = null;
      }

      // Reconnect with backoff: 3s, 6s, 12s, 24s, 30s (max), up to 15 attempts
      const maxRetries = 15;
      if (sseReconnectCount.current < maxRetries) {
        const delay = Math.min(3000 * Math.pow(2, sseReconnectCount.current), 30000);
        sseReconnectCount.current++;
        console.log(`[SSE] Will reconnect in ${delay / 1000}s (attempt ${sseReconnectCount.current}/${maxRetries})`);
        sseReconnectTimer.current = setTimeout(() => {
          sseReconnectTimer.current = null;
          connectSSE();
        }, delay);
      } else {
        console.error('[SSE] Max reconnect attempts reached. Use the Retry button to reconnect.');
      }
    };
  }

  async function pollMargins() {
    try {
      const res = await axios.get(`${API_BASE}/margins/all`);
      const marginsData = res.data.margins || res.data || {};
      const fetchMode = res.data.fetchMode || 'batch';
      setMarginFetchMode(fetchMode);
      const remapped = {};
      Object.entries(marginsData).forEach(([key, data]) => {
        if (data) {
          const tsym = key.includes(':') ? key.split(':')[1] : key;
          remapped[tsym] = { totalMargin: data.total || 0, span: data.span || 0, exposure: data.exposure || 0 };
        }
      });
      setMargins(remapped);
    } catch (err) {}
  }

  async function pollAccountMargins() { try { const res = await axios.get(`${API_BASE}/accounts/margins`); setAccountMargins(res.data); } catch (err) {} }
  async function pollStatus() { try { const res = await axios.get(`${API_BASE}/status`); setConnectionStatus(res.data); } catch (err) {} }

  // Session 46 — Poll user's positions + active orders every 30s so MW can render
  // a small badge next to the Script column showing "you have a position/order on
  // this symbol." Lightweight: positions ~50 items, orders ~20 items per user, both
  // already-existing endpoints. 30s freshness is fine for the badge use case.
  // Orders filtered to active states (OPEN / PENDING / PARTIAL / AMO).
  async function pollMyPositions() {
    try {
      const res = await axios.get(`${API_BASE}/positions`);
      setMyPositions(res.data.positions || []);
    } catch (err) { /* silent — badge just disappears if endpoint hiccups */ }
  }
  async function pollMyOrders() {
    try {
      const res = await axios.get(`${API_BASE}/orders`);
      const all = res.data.orders || res.data || [];
      // Keep only active orders (not COMPLETE/CANCELLED/REJECTED)
      const active = (Array.isArray(all) ? all : []).filter(o => {
        const s = (o.status || '').toUpperCase();
        return s === 'OPEN' || s === 'PENDING' || s === 'PARTIAL' || s === 'AMO' || s === 'CANCELLING';
      });
      setMyOrders(active);
    } catch (err) { /* silent */ }
  }
  // Start the poll cycle on mount
  useEffect(() => {
    pollMyPositions();
    pollMyOrders();
    const posTimer = setInterval(pollMyPositions, 30000);
    const ordTimer = setInterval(pollMyOrders, 30000);
    return () => { clearInterval(posTimer); clearInterval(ordTimer); };
  }, []);
  async function pollConnStatus() {
    try { const res = await axios.get(`${API_BASE}/market-watch/connection-status`); setConnStatus(res.data); }
    catch (err) { setConnStatus({ status: 'offline', dataSource: 'none', message: 'Backend server not responding', details: { backendDown: true } }); }
  }

  // ═══════════════════════════════════════════════════════════
  // FILTER + SORT
  // ═══════════════════════════════════════════════════════════

  const uniqueExpiries = useMemo(() => { const set = new Set(computedRows.map(r => r.expiryStr)); return ['All', ...Array.from(set).sort()]; }, [computedRows]);
  const uniqueSectors = useMemo(() => { const set = new Set(computedRows.map(r => r.sector).filter(Boolean)); return ['All', ...Array.from(set).sort()]; }, [computedRows]);
  const uniqueCategories = useMemo(() => { const set = new Set(computedRows.map(r => r.category).filter(Boolean)); return ['All', ...Array.from(set).sort()]; }, [computedRows]);
  const uniquePublicPrivate = useMemo(() => { const set = new Set(computedRows.map(r => r.publicPrivate).filter(Boolean)); return ['All', ...Array.from(set).sort()]; }, [computedRows]);

  // Session 46 — Build symbol → [badge,...] maps for positions and orders.
  // Memoized so they only recompute when the polled data changes (every 30s), NOT
  // on every tick. Each badge is a small object with the info needed to render:
  //   { direction: 'B'|'S', strike, type: 'CE'|'PE', accountLabel, expiryStr }
  // Multiple positions/orders for the same symbol all show as separate badges,
  // regardless of account or expiry.
  const positionsBySymbol = useMemo(() => {
    const map = new Map();
    for (const p of myPositions) {
      if (!p.symbol || !p.quantity) continue;
      const direction = p.quantity > 0 ? 'B' : 'S';
      const badge = {
        direction, strike: p.strike, type: p.type,
        accountLabel: p.accountLabel || p.account || '',
        expiryStr: p.expiryStr || ''
      };
      const key = p.symbol.toUpperCase();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(badge);
    }
    return map;
  }, [myPositions]);

  const ordersBySymbol = useMemo(() => {
    const map = new Map();
    for (const o of myOrders) {
      const sym = (o.symbol || o.underlying || '').toUpperCase();
      if (!sym) continue;
      const direction = (o.transactionType || o.buyOrSell || '').toUpperCase().startsWith('B') ? 'B' : 'S';
      const badge = {
        direction, strike: o.strike, type: o.type || o.optionType || '',
        accountLabel: o.accountLabel || o.account || '',
        expiryStr: o.expiryStr || o.expiry || ''
      };
      if (!map.has(sym)) map.set(sym, []);
      map.get(sym).push(badge);
    }
    return map;
  }, [myOrders]);

  // Symbols where I have at least one open position. Used by the position filter switch.
  const symbolsWithPositions = useMemo(() => {
    const s = new Set();
    for (const p of myPositions) {
      if (p.symbol && p.quantity) s.add(p.symbol.toUpperCase());
    }
    return s;
  }, [myPositions]);

  // Problem #40 Phase 1: rows are PRE-COMPUTED by the backend.
  // STEP 1: FILTER — runs every second (when computedRows updates via SSE flush).
  // New rows appear/disappear based on filters, but row ORDER stays frozen.
  const filteredRows = useMemo(() => {
    let filtered = [...computedRows];
    if (search.trim()) {
      const terms = search.toLowerCase().split(/\s+/);
      filtered = filtered.filter(row => {
        const haystack = `${row.symbol} ${row.underlying} ${row.type} ${row.strike} ${row.expiryStr} ${row.sector}`.toLowerCase();
        return terms.every(term => haystack.includes(term));
      });
    }
    if (expiryFilter.length > 0) filtered = filtered.filter(r => expiryFilter.includes(r.expiryStr));
    if (typeFilter.length > 0) filtered = filtered.filter(r => typeFilter.includes(r.type));
    if (moneyFilter.length > 0) filtered = filtered.filter(r => {
      if (!r.spot || r.spot === 0) return true;
      let moneyness;
      if (r.strike === r.spot) {
        moneyness = 'ATM';
      } else if (r.type === 'CE') {
        moneyness = r.strike > r.spot ? 'OTM' : 'ITM';
      } else {
        moneyness = r.strike < r.spot ? 'OTM' : 'ITM';
      }
      return moneyFilter.includes(moneyness);
    });
    if (publicPrivateFilter.length > 0) filtered = filtered.filter(r => publicPrivateFilter.includes(r.publicPrivate));
    if (sectorFilter.length > 0) filtered = filtered.filter(r => sectorFilter.includes(r.sector));
    if (categoryFilter.length > 0) filtered = filtered.filter(r => categoryFilter.includes(r.category));
    const minSS = parseFloat(ssMin); const maxSS = parseFloat(ssMax);
    if (!isNaN(minSS)) filtered = filtered.filter(r => r.strikeStockRatio >= minSS);
    if (!isNaN(maxSS)) filtered = filtered.filter(r => r.strikeStockRatio <= maxSS);
    const minOI = parseInt(oiMin); if (!isNaN(minOI)) filtered = filtered.filter(r => r.oi >= minOI);
    const minVol = parseInt(volMin); if (!isNaN(minVol)) filtered = filtered.filter(r => r.volume >= minVol);
    if (resultDateFrom) filtered = filtered.filter(r => r.resultDateISO && r.resultDateISO >= resultDateFrom);
    if (resultDateTo) filtered = filtered.filter(r => r.resultDateISO && r.resultDateISO <= resultDateTo);

    // Session 46 — Position filter switch (3-state). When 'all' (default), pass through.
    // When 'with', keep only rows for symbols where I have an open position. When
    // 'without', keep only rows for symbols where I DON'T have an open position.
    if (positionFilter === 'with') {
      filtered = filtered.filter(r => symbolsWithPositions.has((r.symbol || '').toUpperCase()));
    } else if (positionFilter === 'without') {
      filtered = filtered.filter(r => !symbolsWithPositions.has((r.symbol || '').toUpperCase()));
    }

    // Session 46 — Best-per-underlying-per-type filter. When ON, group remaining
    // rows by (symbol, type=CE/PE) and keep only the row with the highest annualized
    // return per group. Falls back to returnPct if annualized is missing/null.
    // Example: 10 WIPRO rows surviving all filters (5 CE + 5 PE) → reduced to 2 rows
    // (1 best CE + 1 best PE) to reduce visual clutter.
    if (bestPerUnderlying && filtered.length > 0) {
      const bestPerGroup = new Map(); // key: `${symbol}|${type}` → row
      for (const r of filtered) {
        if (!r.symbol || !r.type) continue;
        const key = `${(r.symbol || '').toUpperCase()}|${r.type}`;
        const score = (typeof r.annualized === 'number' && !isNaN(r.annualized)) ? r.annualized
                     : (typeof r.returnPct === 'number' && !isNaN(r.returnPct)) ? r.returnPct
                     : 0;
        const existing = bestPerGroup.get(key);
        const existingScore = existing ? (
          (typeof existing.annualized === 'number' && !isNaN(existing.annualized)) ? existing.annualized
          : (typeof existing.returnPct === 'number' && !isNaN(existing.returnPct)) ? existing.returnPct
          : 0
        ) : -Infinity;
        if (!existing || score > existingScore) bestPerGroup.set(key, r);
      }
      filtered = Array.from(bestPerGroup.values());
    }

    return filtered;
  }, [computedRows, search, expiryFilter, typeFilter, moneyFilter, publicPrivateFilter, sectorFilter, categoryFilter, ssMin, ssMax, oiMin, volMin, resultDateFrom, resultDateTo, positionFilter, symbolsWithPositions, bestPerUnderlying]);

  // STEP 2: SORT — frozen order (shared useFrozenSort). The row ORDER changes only on a sort
  // change, the 60-second auto timer / manual Re-sort (sortVersion), or a filter/SET change
  // (membershipKey). Per-second data updates refresh values in place WITHOUT reshuffling, so rows
  // hold their position for the full 60s instead of dancing every second on live columns.
  const membershipKey = `${search}|${expiryFilter}|${typeFilter}|${moneyFilter}|${publicPrivateFilter}|${sectorFilter}|${categoryFilter}|${ssMin}|${ssMax}|${oiMin}|${volMin}|${resultDateFrom}|${resultDateTo}|${positionFilter}|${bestPerUnderlying}`;
  const rows = useFrozenSort(
    filteredRows, sortCol, sortDir, sortVersion, membershipKey,
    (r) => (r.id != null ? r.id : r.token),
    { resultDate: 'resultDateISO' }
  );

  const displayRows = rows;

  // ═══════════════════════════════════════════════════════════
  // SESSION 47 — Smart token promotion on filter
  // ═══════════════════════════════════════════════════════════
  // The backend (kite-websocket.js) supports promoting REST-polled tokens to
  // real-time WebSocket via POST /api/market-watch/promote-tokens. The endpoint
  // was wired up months ago but the frontend never called it — so visible
  // stocks like Zydus stayed on the 30-second REST polling forever after a
  // search filter, never turning into the green WS dot.
  //
  // Strategy: when the filtered result set is small enough to fit in the WS
  // budget (<= 3000 tokens), promote any visible tokens not already on WS.
  // Debounced 1s so rapid typing in the search box doesn't thrash the backend.
  // Uses the joined-token-list string as the memo key so we only fire when the
  // VISIBLE TOKEN SET changes (filter change), not when row values change
  // (data update once per second from SSE).
  const visibleTokensKey = useMemo(() => {
    if (!filteredRows || filteredRows.length === 0 || filteredRows.length > 3000) return '';
    return filteredRows.map(r => r.token).filter(Boolean).join(',');
  }, [filteredRows]);

  useEffect(() => {
    if (appDemoMode || !sessionId) return;
    if (!visibleTokensKey) return;
    const tokens = visibleTokensKey.split(',').map(Number).filter(Boolean);
    const timer = setTimeout(() => {
      const tokensToPromote = tokens.filter(t => !liveTokens.has(t));
      if (tokensToPromote.length === 0) return;
      // Session id is set globally on axios.defaults by App.js, no per-call header needed.
      axios.post(`${API_BASE}/market-watch/promote-tokens`, { tokens: tokensToPromote })
        .catch(err => console.warn('[MW] promote-tokens failed:', err.message));
    }, 1000);
    return () => clearTimeout(timer);
  }, [visibleTokensKey, liveTokens, sessionId, appDemoMode]);

  // ═══════════════════════════════════════════════════════════
  // SORT HANDLER
  // ═══════════════════════════════════════════════════════════

  function handleSort(col) {
    if (sortCol === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortCol(col); setSortDir('asc'); }
    // Changing sort column/direction triggers an immediate re-sort
    setSortVersion(v => v + 1);
    // Reset the 60-second auto timer so next auto re-sort is 60s from now
    if (autoSortTimerRef.current) clearInterval(autoSortTimerRef.current);
    autoSortTimerRef.current = setInterval(() => setSortVersion(v => v + 1), 60000);
  }

  // Manual re-sort button (also resets the 60-second auto timer)
  function handleResort() {
    setSortVersion(v => v + 1);
    if (autoSortTimerRef.current) clearInterval(autoSortTimerRef.current);
    autoSortTimerRef.current = setInterval(() => setSortVersion(v => v + 1), 60000);
  }

  // ═══════════════════════════════════════════════════════════
  // ORDER POPUP
  // ═══════════════════════════════════════════════════════════

  async function openOrderPopup(row) {
    const acctId = orderAccount || Object.keys(accountMargins)[0] || '';
    // Fetch depth on-demand from old ticks endpoint (depth is stripped from computed rows for performance)
    let rowWithDepth = row;
    if (!row.depth && row.token) {
      try {
        const tickRes = await axios.get(`${API_BASE}/market-watch/ticks`);
        const tickData = tickRes.data[row.token];
        if (tickData?.depth) rowWithDepth = { ...row, depth: tickData.depth };
      } catch (e) { /* depth fetch failed — popup will show dashes, that's OK */ }
    }
    setOrderPopup(rowWithDepth); setOrderMarginData(null); setOrderLots(1);
    setOrderPrice(row.ltp > 0 ? String(row.ltp) : '');
    if (!orderAccount && acctId) setOrderAccount(acctId);
    if (!acctId) return;
    try {
      const res = await axios.post(`${API_BASE}/margins/order-popup`, {
        accountId: acctId, tradingsymbol: row.tradingsymbol, exchange: row.exchange || 'NFO',
        transaction_type: 'SELL', quantity: row.lotSize, price: row.ltp
      });
      setOrderMarginData(res.data);
    } catch (err) { console.error('Failed to get order margin:', err.response?.data || err.message); }
  }

  async function refetchPopupMargin(lots, price) {
    if (!orderPopup || !orderAccount) return;
    setOrderMarginData(null);
    try {
      const res = await axios.post(`${API_BASE}/margins/order-popup`, {
        accountId: orderAccount, tradingsymbol: orderPopup.tradingsymbol, exchange: 'NFO',
        transaction_type: 'SELL', quantity: (lots || 1) * (orderPopup.lotSize || 1), price: price || orderPopup.ltp
      });
      setOrderMarginData(res.data);
    } catch (err) { console.error('Failed to get order margin:', err); }
  }

  async function onAccountChange(newAccountId) {
    setOrderAccount(newAccountId);
    if (!orderPopup) return;
    try {
      const res = await axios.post(`${API_BASE}/margins/order-popup`, {
        accountId: newAccountId, tradingsymbol: orderPopup.tradingsymbol, exchange: 'NFO',
        transaction_type: 'SELL', quantity: orderPopup.lotSize, price: orderPopup.ltp
      });
      setOrderMarginData(res.data);
    } catch (err) { console.error('Failed to get order margin:', err); }
  }

  // ═══════════════════════════════════════════════════════════
  // THEME STYLES
  // ═══════════════════════════════════════════════════════════

  const isDark = theme === 'dark';
  const t = getTheme(isDark);
  const { rowPad, fontSize } = getDensity(density);
  const themeCtx = { t, isDark, rowPad, fontSize };
  // FilterBar's sort dropdown keeps the FULL base list (sorting by a hidden column still
  // works — sort runs on data, not display); only the TABLE renders the user's layout.
  const columns = COLUMNS;
  // Per-user column layout (order / width / hidden) — persisted server-side. See useColumnLayout.js.
  const cols = useColumnLayout('marketwatch_columns', COLUMNS);

  // ═══════════════════════════════════════════════════════════
  // RENDER — NOT INITIALIZED
  // ═══════════════════════════════════════════════════════════

  const retryTimer = useRef(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => { if (appDemoMode && !initialized) loadDemoData(); }, [appDemoMode]);
  useEffect(() => {
    if (!initialized && !appDemoMode) {
      if (retryCount < 30) {
        retryTimer.current = setTimeout(() => { setRetryCount(c => c + 1); loadFullData(); }, 3000);
        return () => clearTimeout(retryTimer.current);
      }
    }
  }, [initialized, appDemoMode, retryCount]);

  if (!initialized) return <MarketWatchLoading themeCtx={themeCtx} retryCount={retryCount} brokerMessage={brokerMessage} />;

  // ═══════════════════════════════════════════════════════════
  // RENDER — MAIN
  // ═══════════════════════════════════════════════════════════

  return (
    <div style={{ fontFamily: "'Segoe UI', -apple-system, sans-serif", background: t.bg, color: t.text, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <ConnectionBanner connStatus={connStatus} themeCtx={themeCtx}
        onRetry={async () => {
          if (connStatus?.details?.backendDown) { loadFullData(); }
          else { try { await axios.post(`${API_BASE}/market-watch/reconnect`); } catch(e) {} pollConnStatus(); }
        }}
      />
      <TopBar connStatus={connStatus} themeCtx={themeCtx}
        demoMode={appDemoMode} initialized={initialized}
        marginFetchMode={marginFetchMode} accountMargins={accountMargins}
        theme={theme} setTheme={setTheme} density={density} setDensity={setDensity}
      />
      <FilterBar
        themeCtx={themeCtx} search={search} setSearch={setSearch}
        moneyFilter={moneyFilter} setMoneyFilter={setMoneyFilter}
        ssMin={ssMin} setSSMin={setSSMin} ssMax={ssMax} setSSMax={setSSMax}
        oiMin={oiMin} setOiMin={setOiMin} volMin={volMin} setVolMin={setVolMin}
        resultDateFrom={resultDateFrom} setResultDateFrom={setResultDateFrom}
        resultDateTo={resultDateTo} setResultDateTo={setResultDateTo}
        sortCol={sortCol} setSortCol={setSortCol} sortDir={sortDir} setSortDir={setSortDir} columns={columns}
        rowCount={rows.length}
        onResort={handleResort}
        positionFilter={positionFilter} setPositionFilter={setPositionFilter}
        bestPerUnderlying={bestPerUnderlying} setBestPerUnderlying={setBestPerUnderlying}
        colsButton={<ColumnsButton cols={cols} isDark={isDark} />}
        downloadButton={<button onClick={() => downloadRowsAsCSV(displayRows, cols.columns, `market_watch_${new Date().toISOString().split('T')[0]}.csv`)} title="Download current view as CSV" className="px-2.5 py-0.5 text-[11px] font-semibold rounded border cursor-pointer bg-emerald-500/15 text-emerald-600 border-emerald-500/30">↓ CSV</button>}
        hasActiveFilters={search || expiryFilter.length > 0 || typeFilter.length > 0 || moneyFilter.length > 0 || publicPrivateFilter.length > 0 || sectorFilter.length > 0 || categoryFilter.length > 0 || ssMin || ssMax || oiMin || volMin || resultDateFrom || resultDateTo || positionFilter !== 'all' || bestPerUnderlying}
        onClearAll={() => {
          setSearch(''); setExpiryFilter([]); setTypeFilter([]); setMoneyFilter([]);
          setPublicPrivateFilter([]); setSectorFilter([]); setCategoryFilter([]); setSSMin(''); setSSMax('');
          setOiMin(''); setVolMin(''); setResultDateFrom(''); setResultDateTo('');
          setPositionFilter('all'); setBestPerUnderlying(false);
        }}
      />
      <DataTable
        themeCtx={themeCtx}
        displayRows={displayRows} rows={rows}
        sortCol={sortCol} sortDir={sortDir} onSort={handleSort}
        onOpenOrderPopup={openOrderPopup}
        liveTokens={liveTokens}
        positionsBySymbol={positionsBySymbol}
        ordersBySymbol={ordersBySymbol}
        columns={cols.columns} onColumnResize={cols.resize}
        filters={{
          expiryFilter, setExpiryFilter, uniqueExpiries,
          typeFilter, setTypeFilter,
          moneyFilter, setMoneyFilter,
          publicPrivateFilter, setPublicPrivateFilter, uniquePublicPrivate,
          sectorFilter, setSectorFilter, uniqueSectors,
          categoryFilter, setCategoryFilter, uniqueCategories,
        }}
      />
      {orderPopup && (
        <OrderPopup
          themeCtx={themeCtx} row={orderPopup}
          orderLots={orderLots} setOrderLots={setOrderLots}
          orderPrice={orderPrice} setOrderPrice={setOrderPrice}
          orderMarginData={orderMarginData} orderAccount={orderAccount}
          onAccountChange={onAccountChange} accountMargins={accountMargins}
          margins={margins} onRefetchMargin={refetchPopupMargin}
          onClose={() => setOrderPopup(null)}
          user={user}
        />
      )}
    </div>
  );
}
